import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Customers.css';
import { fetchAllCustomers } from '../../utils/fetchAllPages';

const CustomersList = forwardRef(({ onSelectCustomer, selectedCustomerId, onNew }, ref) => {
  const { t } = useTranslation('customers');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Expose fetchCustomers method to parent components
  useImperativeHandle(ref, () => ({
    fetchCustomers
  }));

  useEffect(() => {
    fetchCustomers();
  }, [statusFilter]);

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: fetchCustomers
  }));

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      // Every customer, not just the API's first page of 100; search and status filter below (#310)
      setCustomers(await fetchAllCustomers());
      setError(null);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError(t('list.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = (customer.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          customer.customer_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          customer.phone?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && customer.is_active) ||
                         (statusFilter === 'inactive' && !customer.is_active);

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="status-badge status-active">{t('view.status.active')}</span>
    ) : (
      <span className="status-badge status-inactive">{t('view.status.inactive')}</span>
    );
  };


  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && customers.length === 0) {
    return <div className="list-container"><div className="loading">{t('list.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('list.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('toolbar.buttons.newCustomer')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('list.filters.allStatus')}</option>
            <option value="active">{t('list.filters.active')}</option>
            <option value="inactive">{t('list.filters.inactive')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('list.headers.code')}</th>
              <th>{t('list.headers.customerName')}</th>
              <th>{t('list.headers.contact')}</th>
              <th>{t('list.headers.creditLimit')}</th>
              <th>{t('list.headers.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  {searchTerm ? t('list.noData.noResults') : t('list.noData.noCustomers')}
                </td>
              </tr>
            ) : (
              filteredCustomers.map(customer => (
                <tr
                  key={customer.customer_id}
                  onClick={() => onSelectCustomer(customer.customer_id)}
                  className={selectedCustomerId === customer.customer_id ? 'selected' : ''}
                >
                  <td className="customer-code">
                    <code>{customer.customer_code || t('view.defaultValues.notAvailable')}</code>
                  </td>
                  <td className="customer-name">
                    <strong>{customer.customer_name}</strong>
                    {customer.email && (
                      <div className="customer-email">{customer.email}</div>
                    )}
                  </td>
                  <td className="contact">
                    {customer.phone ? (
                      <div className="contact-phone">{customer.phone}</div>
                    ) : (
                      <div className="text-muted">{t('view.defaultValues.notAvailable')}</div>
                    )}
                    {customer.email && (
                      <div className="customer-email">{customer.email}</div>
                    )}
                  </td>
                  <td className="credit-limit">
                    {formatCurrency(customer.credit_limit)}
                  </td>
                  <td>
                    {getStatusBadge(customer.is_active)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('list.footer.recordCount', { filtered: filteredCustomers.length, total: customers.length })}
        </div>
      </div>
    </div>
  );
});

CustomersList.displayName = 'CustomersList';

export default CustomersList;